import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type DepartmentItem = { id: string; name: string };

const statusOptions = [
  { label: "ທັງໝົດ", value: "all" },
  { label: "ໃຊ້ງານ", value: "active" },
  { label: "ປັບປຸງ", value: "deprecated" },
  { label: "ບໍ່ໃຊ້ງານ", value: "archived" },
];

export function TemplateFilter() {
  const nav = useNavigate({ from: "/app/template" });
  const search = useSearch({ from: "/app/template" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const nameFilter = findCondition(filters, "name");
  const departmentFilter = findCondition(filters, "departmentId");
  const statusFilter = findCondition(filters, "status");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || "",
  );

  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>(
    (departmentFilter?.value as string) || "",
  );

  const [selectedStatus, setSelectedStatus] = useState<string>(
    (statusFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((nameFilter?.value as string) || ""),
    [nameFilter?.value],
  );

  useEffect(
    () => setSelectedDepartmentId((departmentFilter?.value as string) || ""),
    [departmentFilter?.value],
  );

  useEffect(
    () => setSelectedStatus((statusFilter?.value as string) || ""),
    [statusFilter?.value],
  );

  const debouncedName = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "name",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "name");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const handleDepartmentChange = (departmentId: string) => {
    setSelectedDepartmentId(departmentId);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (departmentId) {
      nextFilters = upsertCondition(filters, {
        field: "departmentId",
        op: "eq",
        value: departmentId,
      });
    } else {
      nextFilters = removeConditions(filters, "departmentId");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (status !== "all") {
      nextFilters = upsertCondition(filters, {
        field: "status",
        op: "eq",
        value: status,
      });
    } else {
      nextFilters = removeConditions(filters, "status");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  };

  const hasFilter =
    !!nameFilter?.value || !!selectedDepartmentId || !!statusFilter?.value;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2" data-tourid="template-filter">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາຕາມຊື່ແມ່ແບບ..."
            defaultValue={searchValue}
            onChange={(e) => debouncedName(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />

          <InfiniteCombobox<DepartmentItem>
            value={selectedDepartmentId}
            onValueChange={handleDepartmentChange}
            queryKey={["departments-lookup"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກພະແນກ"
            className="h-8 sm:max-w-2xs"
            clearable
          />

          <SimpleSelect
            value={selectedStatus}
            onValueChange={handleStatusChange}
            placeholder="ເລືອກສະຖານະ"
            options={statusOptions}
            className="h-8 sm:max-w-2xs"
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
